import { lazy, Suspense, useDeferredValue, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { DRINK_KIND_LABELS, DRINK_KINDS, type Bottle, type DrinkKind } from '../../shared/api';
import { BottleList } from '../components/BottleList';
import { useResource } from '../hooks/useResource';
import { useStoredState } from '../hooks/useStoredState';
import { api, errorMessage, EXPORT_URLS } from '../lib/api';
import { isWholeUnit, needsCheck, onShoppingList, unitsLeft } from '../lib/bottles';
import { matchesText } from '../lib/search';

const ScanFinder = lazy(() => import('../components/ScanFinder'));

type Filter = 'in_stock' | 'open' | 'check' | 'shopping' | 'finished' | 'all';
type KindFilter = 'all' | DrinkKind;
type Sort = 'newest' | 'name' | 'least' | 'kind';

const SORTS: ReadonlyArray<[Sort, string, (a: Bottle, b: Bottle) => number]> = [
  ['newest', 'Newest first', (a, b) => b.addedAt.localeCompare(a.addedAt) || b.id - a.id],
  ['name', 'Name A–Z', (a, b) => a.name.localeCompare(b.name) || (a.variant ?? '').localeCompare(b.variant ?? '')],
  ['least', 'Least left first', (a, b) => unitsLeft(a) - unitsLeft(b) || a.name.localeCompare(b.name)],
  ['kind', 'By kind', (a, b) => a.kind.localeCompare(b.kind) || a.name.localeCompare(b.name)],
];

const FILTERS: ReadonlyArray<[Filter, string, (bottle: Bottle) => boolean]> = [
  ['in_stock', 'In stock', (bottle) => bottle.quantity > 0],
  ['open', 'Open', (bottle) => bottle.quantity > 0 && !isWholeUnit(bottle) && bottle.level < 0.99],
  ['check', 'Worth checking', (bottle) => needsCheck(bottle)],
  ['shopping', 'Shopping list', onShoppingList],
  ['finished', 'Finished', (bottle) => bottle.quantity === 0],
  ['all', 'Everything', () => true],
];

export function BarPage() {
  const { data: bottles, error, loading, reload } = useResource('bottles', api.listBottles);
  const [query, setQuery] = useState('');
  // Filters and sort survive opening a bottle and coming back (and the next visit).
  const [filter, setFilter] = useStoredState<Filter>('my-catalogue:bar-filter', 'in_stock', FILTERS.map(([key]) => key));
  const [kind, setKind] = useStoredState<KindFilter>('my-catalogue:bar-kind', 'all', ['all', ...DRINK_KINDS]);
  const [sort, setSort] = useStoredState<Sort>('my-catalogue:bar-sort', 'newest', SORTS.map(([key]) => key));
  const [finding, setFinding] = useState(false);
  const navigate = useNavigate();

  /** A barcode read in the list: open the one bottle with it, show the several, or offer to add it. */
  function findByBarcode(barcode: string) {
    const matches = (bottles ?? []).filter((bottle) => bottle.barcode === barcode);
    if (matches.length === 1) navigate(`/bar/${matches[0]!.id}`);
    else if (matches.length > 1) {
      setFilter('all');
      setQuery(barcode);
      setFinding(false);
    } else navigate(`/scan?scan=drinks&barcode=${barcode}`);
  }
  const deferredQuery = useDeferredValue(query.trim());

  const counts = useMemo(
    () => Object.fromEntries(FILTERS.map(([key, , test]) => [key, (bottles ?? []).filter(test).length])) as Record<Filter, number>,
    [bottles],
  );

  const kindCounts = useMemo(() => {
    const counts = Object.fromEntries(DRINK_KINDS.map((k) => [k, 0])) as Record<DrinkKind, number>;
    for (const bottle of bottles ?? []) counts[bottle.kind] += 1;
    return counts;
  }, [bottles]);

  const visible = useMemo(() => {
    const test = FILTERS.find(([key]) => key === filter)![2];
    const compare = SORTS.find(([key]) => key === sort)![2];
    return (bottles ?? [])
      .filter(
        (bottle) =>
          test(bottle) &&
          (kind === 'all' || bottle.kind === kind) &&
          matchesText([bottle.name, bottle.brand, bottle.variant, bottle.barcode], deferredQuery),
      )
      .sort(compare);
  }, [bottles, filter, kind, sort, deferredQuery]);

  if (bottles === null) {
    if (loading) return <p className="page-status">Loading bar…</p>;
    return (
      <div className="page-status" role="alert">
        <p className="error-text">{errorMessage(error)}</p>
        <button type="button" className="btn btn--primary" onClick={reload}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <section className="collection-page">
      <header className="page-header">
        <div>
          <h1>Bar</h1>
          <p className="muted">
            {counts.in_stock} in stock
            {counts.check > 0 && ` · ${counts.check} worth checking`}
            {counts.shopping > 0 && ` · ${counts.shopping} to buy`}
            {counts.finished > 0 && ` · ${counts.finished} finished`}
          </p>
        </div>
        <div className="export-buttons">
          <Link className="btn btn--primary btn--small" to="/scan?scan=drinks">
            Scan a bottle
          </Link>
          <a className="btn btn--secondary btn--small" href={EXPORT_URLS.barJson} download>
            Export JSON
          </a>
          <a className="btn btn--secondary btn--small" href={EXPORT_URLS.barCsv} download>
            Export CSV
          </a>
        </div>
      </header>

      {error !== null && (
        <p className="error-text" role="alert">
          Couldn't refresh: {errorMessage(error)}
        </p>
      )}

      {bottles.length === 0 ? (
        <div className="empty-state">
          <p>Nothing in the bar yet.</p>
          <Link className="btn btn--primary" to="/scan?scan=drinks">
            Scan your first bottle
          </Link>
        </div>
      ) : (
        <>
          <div className="filters">
            <div className="filters__row">
              <input
                type="search"
                className="filters__search"
                placeholder="Search name, brand or vintage"
                aria-label="Search the bar"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <button
                type="button"
                className={`btn btn--small filters__scan${finding ? ' btn--primary' : ' btn--secondary'}`}
                aria-pressed={finding}
                title="Find a bottle by scanning its barcode"
                onClick={() => setFinding((open) => !open)}
              >
                <span aria-hidden="true">📷</span> Find
              </button>
            </div>
            {finding && (
              <Suspense fallback={<p className="hint filters__wide">Loading camera…</p>}>
                <ScanFinder kind="drinks" onCode={findByBarcode} onClose={() => setFinding(false)} />
              </Suspense>
            )}
            <select className="filters__status" aria-label="Filter by kind" value={kind} onChange={(event) => setKind(event.target.value as KindFilter)}>
              <option value="all">All kinds</option>
              {DRINK_KINDS.map((option) => (
                <option key={option} value={option} disabled={kindCounts[option] === 0}>
                  {DRINK_KIND_LABELS[option]} ({kindCounts[option]})
                </option>
              ))}
            </select>
            <select className="filters__status" aria-label="Filter by status" value={filter} onChange={(event) => setFilter(event.target.value as Filter)}>
              {FILTERS.map(([key, label]) => (
                <option key={key} value={key}>
                  {label} ({counts[key]})
                </option>
              ))}
            </select>
            <select className="filters__status" aria-label="Sort" value={sort} onChange={(event) => setSort(event.target.value as Sort)}>
              {SORTS.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          {visible.length === 0 ? <p className="hint">No bottles match.</p> : <BottleList bottles={visible} linkTo={(b) => `/bar/${b.id}`} />}
        </>
      )}
    </section>
  );
}
